import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spin, message } from 'antd';
import styled from 'styled-components';
import ReadOnlyDocumentViewer from '../Document/ReadOnlyDocumentViewer';
import { pagePointAtClient, startBoxDrag } from '../ESignModal/boxDrag';
import { selectBoxSoon } from '../ESignModal/esignBoxes';
import { applyAttorneySignature, fetchAttorneySignatures } from '../../service/settings';
import { announceDocumentReplaced, prepareDocumentReplace } from '../../utils/documentReplaced';
import {
  DEFAULT_SIGNATURE_WIDTH,
  accreditationData,
  accreditationPlacementAt,
  createAccreditationStamp,
  createDateStamp,
  createSignatureStamp,
  datePlacementAt,
  isAccreditationStamp,
  isDateStamp,
  placedSignatureAttorneyNames,
  placedSignatures,
  readSignaturePlacements,
  signatureOwner,
  signaturePlacementAt,
  stampLabel,
  todayText,
} from './attorneySignatureStamps';

const ACCENT = '#2563EB';

const Layout = styled.div`
  display: flex;
  gap: 16px;
  height: 78vh;
`;

const Panel = styled.aside`
  width: 260px;
  flex-shrink: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Viewer = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const SignatureCard = styled.button`
  display: block;
  width: 100%;
  margin-bottom: 8px;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:hover:not(:disabled) {
    border-color: ${ACCENT};
  }

  &:disabled {
    cursor: not-allowed;
    background: #f9fafb;
  }

  img {
    display: block;
    max-width: 100%;
    max-height: 56px;
    margin-bottom: 4px;
    pointer-events: none;
  }
`;

const CardName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #111827;
`;

const DateText = styled.div`
  font-family: Helvetica, Arial, sans-serif;
  font-size: 16px;
  color: #111827;
  padding: 6px 0;
  text-align: center;
  border-bottom: 1px solid #111827;
  margin-bottom: 4px;
`;

const Help = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const PlacedRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #f3f4f6;
`;

const AttorneySignatureModal = ({ open, document, onCancel, onSigned }) => {
  const [instance, setInstance] = useState(null);
  const [attorneys, setAttorneys] = useState([]);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);
  const [placed, setPlaced] = useState([]);
  const [signing, setSigning] = useState(false);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setInstance(null);
      setPlaced([]);
      return undefined;
    }
    let active = true;
    setLoadingAttorneys(true);
    fetchAttorneySignatures()
      .then((res) => active && setAttorneys(res?.data?.attorneys || []))
      .catch(() => active && message.error("Couldn't load the attorney signatures — try again."))
      .finally(() => active && setLoadingAttorneys(false));
    return () => {
      active = false;
    };
  }, [open]);

  const handleInstanceReady = useCallback((inst) => {
    inst.Core.annotationManager.disableReadOnlyMode?.();
    inst.UI.setToolMode?.(inst.Core.Tools?.ToolNames?.EDIT || 'AnnotationEdit');
    setInstance(inst);
  }, []);

  useEffect(() => {
    if (!instance) return undefined;
    const refresh = () => {
      const current = placedSignatures(instance);
      const owners = placedSignatureAttorneyNames(current);
      const orphans = current.filter((annotation) => {
        const acc = accreditationData(annotation);
        return acc && !owners.has(acc.attorney_name);
      });
      if (orphans.length) {
        // Drop accreditation numbers whose matching signature was removed.
        orphans.forEach((annotation) => instance.Core.annotationManager.deleteAnnotation(annotation));
        return;
      }
      setPlaced(current);
    };
    refresh();
    instance.Core.annotationManager.addEventListener('annotationChanged', refresh);
    return () => instance.Core.annotationManager.removeEventListener('annotationChanged', refresh);
  }, [instance]);

  const addSignature = async (attorney, placement = null) => {
    if (!instance) return;
    try {
      const annotation = await createSignatureStamp(instance, attorney, placement);
      selectBoxSoon(instance, annotation);
    } catch {
      message.error("Couldn't place that signature — try again.");
    }
  };

  const dropSignature = (attorney, clientX, clientY) => {
    const point = pagePointAtClient(instance, clientX, clientY);
    if (!point) {
      message.info('Drop the signature on the page where it should go.');
      return;
    }
    addSignature(attorney, signaturePlacementAt(instance, attorney, point));
  };

  const beginDrag = (attorney, event) => {
    if (!instance || signing) return;
    startBoxDrag(event, {
      label: attorney.attorney_name,
      color: ACCENT,
      imageUrl: attorney.image_data_url,
      imageWidth: DEFAULT_SIGNATURE_WIDTH,
      onDrop: (x, y) => dropSignature(attorney, x, y),
    });
  };

  const addDate = async (placement = null) => {
    if (!instance) return;
    try {
      const annotation = await createDateStamp(instance, placement);
      selectBoxSoon(instance, annotation);
    } catch {
      message.error("Couldn't place the date — try again.");
    }
  };

  const beginDateDrag = (event) => {
    if (!instance || signing) return;
    startBoxDrag(event, {
      label: todayText(),
      color: ACCENT,
      onDrop: (clientX, clientY) => {
        const point = pagePointAtClient(instance, clientX, clientY);
        if (!point) {
          message.info('Drop the date on the page where it should go.');
          return;
        }
        addDate(datePlacementAt(instance, point));
      },
    });
  };

  const addAccreditation = async (attorney, placement = null) => {
    if (!instance) return;
    const owners = placedSignatureAttorneyNames(placedSignatures(instance));
    if (!owners.has(attorney.attorney_name)) {
      message.info(`Place ${attorney.attorney_name}'s signature first, then add their accreditation number.`);
      return;
    }
    try {
      const annotation = await createAccreditationStamp(instance, attorney, placement);
      selectBoxSoon(instance, annotation);
    } catch {
      message.error("Couldn't place the accreditation number — try again.");
    }
  };

  const beginAccreditationDrag = (attorney, event) => {
    if (!instance || signing) return;
    const owners = placedSignatureAttorneyNames(placedSignatures(instance));
    if (!owners.has(attorney.attorney_name)) {
      message.info(`Place ${attorney.attorney_name}'s signature first, then add their accreditation number.`);
      return;
    }
    const number = (attorney.accreditation_number || '').trim();
    startBoxDrag(event, {
      label: `${attorney.attorney_name} #${number}`,
      color: ACCENT,
      onDrop: (clientX, clientY) => {
        const point = pagePointAtClient(instance, clientX, clientY);
        if (!point) {
          message.info('Drop the accreditation number on the page where it should go.');
          return;
        }
        addAccreditation(attorney, accreditationPlacementAt(instance, point));
      },
    });
  };

  const removeSignature = (annotation) => {
    instance?.Core.annotationManager.deleteAnnotation(annotation);
  };

  const handleSign = async () => {
    if (!instance || !document?.id || inFlightRef.current) return;
    const placements = readSignaturePlacements(instance);
    if (!placements.length) return;
    inFlightRef.current = true;
    setSigning(true);
    let replaced = false;
    try {
      await prepareDocumentReplace(document.id);
      const res = await applyAttorneySignature(document.id, placements);
      replaced = true;
      message.success('Signed. The signed copy is now the current version.');
      onSigned(res?.data?.document || document);
    } catch (err) {
      message.error(err?.response?.data?.error || err?.response?.data?.detail || "Couldn't sign the document — try again.");
    } finally {
      announceDocumentReplaced(document.id, { replaced });
      inFlightRef.current = false;
      setSigning(false);
    }
  };

  const available = attorneys.filter((a) => a.has_signature_image && a.image_data_url);
  const missing = attorneys.filter((a) => !(a.has_signature_image && a.image_data_url));
  const signatureOwnersOnPage = placedSignatureAttorneyNames(placed);
  const withAccreditation = available.filter(
    (a) => (a.accreditation_number || '').trim() && signatureOwnersOnPage.has(a.attorney_name),
  );
  const count = placed.length;
  const signatureCount = placed.filter((a) => Boolean(signatureOwner(a))).length;
  const signLabel = () => {
    if (signatureCount === 0 && count > 0) return 'Add placements';
    return signatureCount > 1 ? `Sign — ${signatureCount} signatures` : 'Sign';
  };

  return (
    <Modal
      open={open}
      title={`Add attorney signature${document?.name ? ` — ${document.name}` : ''}`}
      width="95vw"
      style={{ top: 16 }}
      onCancel={() => !signing && onCancel()}
      maskClosable={!signing}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={signing}>Cancel</Button>,
        <Button
          key="sign"
          type="primary"
          onClick={handleSign}
          loading={signing}
          disabled={!instance || count === 0}
          data-testid="attorney-signature-sign"
        >
          {signLabel()}
        </Button>,
      ]}
    >
      <Layout>
        <Panel>
          <div>
            <SectionTitle>Signatures</SectionTitle>
            {loadingAttorneys && <Spin size="small" />}
            {available.map((attorney) => (
              <SignatureCard
                key={attorney.attorney_name}
                type="button"
                disabled={!instance || signing}
                onPointerDown={(event) => beginDrag(attorney, event)}
                onClick={() => addSignature(attorney)}
                data-testid={`attorney-signature-card-${attorney.attorney_name}`}
              >
                <img src={attorney.image_data_url} alt={`${attorney.attorney_name} signature`} />
                <CardName>{attorney.attorney_name}</CardName>
              </SignatureCard>
            ))}
            {!loadingAttorneys && available.length === 0 && (
              <Help>No signatures are on file yet. An administrator adds them in Settings, Attorney Signatures.</Help>
            )}
            {missing.length > 0 && available.length > 0 && (
              <Help>
                Not on file yet: {missing.map((a) => a.attorney_name).join(', ')}. An administrator adds them in
                Settings, Attorney Signatures.
              </Help>
            )}
          </div>
          <div>
            <SectionTitle>Date</SectionTitle>
            <SignatureCard
              type="button"
              disabled={!instance || signing}
              onPointerDown={beginDateDrag}
              onClick={() => addDate()}
              data-testid="attorney-signature-date-card"
            >
              <DateText>{todayText()}</DateText>
              <CardName>Today&apos;s date</CardName>
            </SignatureCard>
          </div>
          {withAccreditation.length > 0 && (
            <div>
              <SectionTitle>Accreditation number</SectionTitle>
              {withAccreditation.map((attorney) => (
                <SignatureCard
                  key={`acc-${attorney.attorney_name}`}
                  type="button"
                  disabled={!instance || signing}
                  onPointerDown={(event) => beginAccreditationDrag(attorney, event)}
                  onClick={() => addAccreditation(attorney)}
                  data-testid={`attorney-accreditation-card-${attorney.attorney_name}`}
                >
                  <DateText>{attorney.accreditation_number}</DateText>
                  <CardName>{attorney.attorney_name}</CardName>
                </SignatureCard>
              ))}
            </div>
          )}
          <Help>
            Drag a signature, date, or accreditation number onto its line, then pull a corner to size it.
            Clicking one instead drops it in the middle of the page. An accreditation number is only offered
            after that attorney&apos;s signature is on the page. Select a box and press Delete to remove it.
          </Help>
          <div>
            <SectionTitle>On this document</SectionTitle>
            {count === 0 && <Help>None yet.</Help>}
            {placed.map((annotation, index) => (
              <PlacedRow key={annotation.Id || index}>
                <span>{stampLabel(annotation)} · p.{annotation.PageNumber}</span>
                <Button size="small" type="link" onClick={() => removeSignature(annotation)} disabled={signing}>
                  Remove
                </Button>
              </PlacedRow>
            ))}
          </div>
          <Help>
            Signing saves a new version of this PDF with the signatures, dates, and accreditation numbers built in,
            so they can&apos;t be moved or removed afterwards. The unsigned original is kept.
          </Help>
        </Panel>
        <Viewer>
          {open && (
            <ReadOnlyDocumentViewer
              documentId={document?.id}
              filename={document?.name}
              fileType="pdf"
              onInstanceReady={handleInstanceReady}
            />
          )}
        </Viewer>
      </Layout>
    </Modal>
  );
};

AttorneySignatureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  document: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onCancel: PropTypes.func.isRequired,
  onSigned: PropTypes.func.isRequired,
};

AttorneySignatureModal.defaultProps = {
  document: null,
};

export default AttorneySignatureModal;
